import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  TrendingUp, 
  Users, 
  Clock, 
  ShoppingBag, 
  Shield,
  ArrowRight,
  Lightbulb,
  Wrench,
  Zap
} from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CaseStudies = () => {
  const outcomes = [
    {
      id: 1,
      challenge: "Sales dip despite stable footfall",
      icon: TrendingUp,
      color: "from-cyan-500 to-blue-500",
      insight: "Staff–customer interactions dropped during peak hours (7–9 pm), increasing \"walkout without billing.\"",
      solution: "Zone-wise interaction + idle-time tracking; peak-hour staffing reallocation + daily manager coaching prompts.",
      impacts: [
        { value: "+3–5 pp", label: "Conversion" },
        { value: "+8–12%", label: "Same-store sales" }
      ],
      timeline: "6–8 weeks"
    },
    {
      id: 2,
      challenge: "High trial-room traffic, low purchases",
      icon: ShoppingBag,
      color: "from-purple-500 to-pink-500",
      insight: "Fitting-room to billing drop-off spiked when fitting-room assistance coverage fell below threshold.",
      solution: "Fitting-room queue + assistance monitoring; assign runner + auto-alert when queue/coverage deviates.",
      impacts: [
        { value: "-20–30%", label: "Fitting-room wait" },
        { value: "+6–10%", label: "Bills from trials" }
      ],
      timeline: "4–6 weeks"
    },
    {
      id: 3,
      challenge: "Customer dwell time shrinking",
      icon: Clock,
      color: "from-orange-500 to-red-500",
      insight: "Dwell time fell 10–15% in key zones (e.g., denim/occasionwear), with early exits after first 2 minutes.",
      solution: "Heatmaps + zone drop-off diagnostics; VM reset + \"newness\" placement + staff zone-ownership.",
      impacts: [
        { value: "+10–18%", label: "Dwell time" },
        { value: "+5–9%", label: "Sales uplift" }
      ],
      timeline: "3–5 weeks"
    },
    {
      id: 4,
      challenge: "Weekend queues causing lost sales",
      icon: Users,
      color: "from-green-500 to-teal-500",
      insight: "Queue wait time crossed 6–8 min during spikes; queue abandonment increased.",
      solution: "Live queue measurement; counter-opening triggers + staffing playbook 20 minutes before predicted surges.",
      impacts: [
        { value: "-25–40%", label: "Wait time" },
        { value: "+4–7%", label: "Bills/hour" }
      ],
      timeline: "4–6 weeks"
    },
    {
      id: 5,
      challenge: "Shrinking margins from shrinkage + SOP lapses",
      icon: Shield,
      color: "from-indigo-500 to-purple-500",
      insight: "High-risk zones had repeated anomalies; late open/early close patterns correlated with higher variance.",
      solution: "SOP adherence tracking + risk-zone alerts + incident clips for fast review.",
      impacts: [
        { value: "-15–25%", label: "Shrinkage" },
        { value: "-70–90%", label: "SOP breaches" }
      ],
      timeline: "30–90 days"
    }
  ];

  return (
    <div data-testid="case-studies-page" className="bg-white">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent"></div>
        <div className="container-custom relative z-10 text-center">
          <AnimatedSection>
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-cyan-400 text-xs font-bold uppercase tracking-wider mb-4 border border-white/10">
              Real Results
            </span>
            <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-white mb-6 leading-tight">
              Client <span style={{
                background: 'linear-gradient(90deg, #00D4FF 0%, #7B68EE 50%, #FF1493 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Outcomes</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              See how ProAct AI transforms retail challenges into measurable business impact.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Outcomes Grid */}
      <section className="py-16 lg:py-20">
        <div className="container-custom">
          <div className="space-y-8">
            {outcomes.map((outcome, i) => (
              <AnimatedSection key={outcome.id} delay={i * 0.1}>
                <div 
                  data-testid={`outcome-card-${i}`}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Card Header */}
                  <div className={`bg-gradient-to-r ${outcome.color} p-6 md:p-8`}>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                        <outcome.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <span className="text-white/70 text-sm font-medium">Challenge #{outcome.id}</span>
                        <h3 className="text-xl md:text-2xl font-bold text-white">{outcome.challenge}</h3>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 md:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Insight */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Lightbulb className="w-4 h-4 text-amber-600" />
                          </div>
                          <span className="font-bold text-gray-900">ProAct AI Insight</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">{outcome.insight}</p>
                      </div>

                      {/* Solution */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Wrench className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="font-bold text-gray-900">Solution</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">{outcome.solution}</p>
                      </div>

                      {/* Impact */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <Zap className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="font-bold text-gray-900">Impact</span>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {outcome.impacts.map((impact, idx) => (
                            <div 
                              key={idx}
                              className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
                            >
                              <div className={`text-xl font-bold bg-gradient-to-r ${outcome.color} bg-clip-text text-transparent`}>
                                {impact.value}
                              </div>
                              <div className="text-xs text-gray-500 font-medium">{impact.label}</div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>Results in <strong className="text-gray-700">{outcome.timeline}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <AnimatedSection>
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-purple-900/30 via-transparent to-transparent"></div>
              <div className="relative z-10">
                <h2 className="text-2xl md:text-4xl font-bold font-heading text-white mb-4">
                  Ready to achieve similar results?
                </h2>
                <p className="text-white/70 mb-8 max-w-xl mx-auto">
                  Let's discuss how ProAct AI can solve your specific retail challenges.
                </p>
                <Link 
                  to="/contact" 
                  data-testid="case-studies-cta"
                  className="inline-flex items-center px-8 py-4 rounded-full font-bold text-white shadow-lg hover:shadow-xl transition-all"
                  style={{ background: 'linear-gradient(90deg, #00D4FF 0%, #7B68EE 50%, #FF1493 100%)' }}
                >
                  Book a Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
